import getHolidayList from 'helpers/getHolidayList';
import moment from 'moment-timezone';

// This can be used to identify name of the day with day number
const DAYS_MAP = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday'
};

/**
 * @description Generates an array of days between given dates. Array will have actual days, ie `["Monday", "Tuesday" ... ]`
 * @param {object} args
 * @param {moment.Moment} args.start Moment object
 * @param {moment.Moment} args.end Moment object
 * @returns {string[]} Array of days between start and end dates
 */
export const getDaysBetweenTwoDates = ({ start, end }) => {
  // Info: daysInPeriod represents the actual days between start and end date
  const daysInPeriod = [];

  const currentDate = start.clone();

  while (currentDate.isBefore(end, 'days')) {
    const dayNumber = currentDate.isoWeekday();

    daysInPeriod.push(DAYS_MAP[dayNumber]);
    currentDate.add(1, 'day');
  }

  return daysInPeriod;
};

/**
 *
 * @param {number | undefined} seconds time in seconds
 * @return {number} equavalnt hours
 */
export const secondsToHours = seconds => {
  if (!seconds) return 0;

  return Number(seconds) / 60 / 60;
};

/**
 *
 * @param {number} hours
 * @returns
 */
export const hoursToSeconds = hours => {
  if (!hours) return 0;

  return Number(hours) * 60 * 60;
};

/**
 * @description Calculates the total number of months between two dates, inclusive of the start and end date.
 *
 * @param {Object} args
 * @param {String} args.start
 * @param {String} args.end
 * @returns {Number} Total number of months the given dates, inclusive.
 *
 *
 * Note: moment does some internal 'special handling' that doesn't give exact months.
 * Moment’s .diff() method measures only complete months between the two dates, and because it compares boundaries.
 * From December 1, 2024 to February 29, 2024, the month boundaries are:
 * Eg:
 * From December 1, 2024 to January 1, 2025 (1 full month).
 * From January 1, 2025 to February 1, 2025 (1 full month).
 * Therefore, the difference is 2 full months so adding +1 to the difference
 *
 * Refer:
 * https://momentjs.com/docs/#/displaying/difference/
 * https://stackoverflow.com/a/19705548
 */
export const getMonthCountInclusive = ({ start, end }) => {
  const monthCount =
    start && end ? moment(end).endOf('month').diff(moment(start).startOf('month'), 'month') + 1 : 0;
  return monthCount;
};

export const isHoliday = date => {
  if (!date) return false;

  const holidayList = getHolidayList();

  return holidayList.some(holiday => holiday.date === date.format('YYYY-MM-DD'));
};

export const isWeekend = date => {
  if (!date) return false;

  const SATURDAY = 6;
  const SUNDAY = 7;

  return date.isoWeekday() === SATURDAY || date.isoWeekday() === SUNDAY;
};

export const getPreviousMonth = date => {
  if (!date) return { startDate: undefined, endDate: undefined };

  const previousMonth = moment(date).subtract(1, 'month');

  const startDate = previousMonth.startOf('month').toDate();
  const endDate = previousMonth.endOf('month').toDate();

  return { startDate, endDate };
};
